/* eslint-disable no-alert */
/* eslint-disable no-unused-expressions */
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Drawer,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import { AddIcon, AddImageIcon, CourseIcon, EditIcon } from 'src/v3/core/icons'

import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchSections,
  selectSectionsStatus,
  selectSectionsByLinkedService,
  selectActiveLinkedService,
  activeSectionChanged,
  selectActiveSection,
  fetchCourses,
  selectCoursesStatus,
  selectCourses,
  fetchLinkedServices,
  selectLinkedServicesByIds,
  selectLinkedServicesStatus,
} from 'src/v3/create/create.slice'

import { useFormik } from 'formik'
import * as yup from 'yup'
import axios from 'src/utilities/http-client'
import { compose, F, T } from 'ramda'
import { processImageFile, uploadImageToS3 } from 'src/v3/utilities/s3'
import useSession from 'src/authentication/hooks/use-session'
import noop from 'src/utilities/noop'
import { SelectLinkedService } from 'src/v3/create/components/select-entities'
import ConfirmAction from 'src/v3/components/confirm-action'
import Loading from 'src/v3/components/loading'

export default (): ReactElement => {
  let session = useSession()
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let linkedServices = useAppSelector(
    selectLinkedServicesByIds(session?.linkedServices ?? []),
  )
  let activeSection = useAppSelector(selectActiveSection)
  let sectionsStatus = useAppSelector(selectSectionsStatus)
  let sections = useAppSelector(
    selectSectionsByLinkedService(activeLinkedService),
  )
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let courses = useAppSelector(selectCourses)

  let [saving, setSaving] = useState(false)
  let [formOpen, setFormOpen] = useState(false)
  let [uploading, setUploading] = useState<string>('')
  let [deleting, setDeleting] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      description: yup.string().required(),
      // Should be required when we add new libraries
      // library: yup.string().required(),
      linkedService: yup.string().required(),
      pageRank: yup.number(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      name: '',
      description: '',
      library: '0f6846fb-027d-4bde-899a-e8beb373e668',
      linkedService: '',
      pageRank: 0,
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!values.id) {
        axios
          .post('/api/v2/create/section', {
            name: values.name,
            description: values.description,
            libraryId: '0f6846fb-027d-4bde-899a-e8beb373e668',
            linkedServiceId:
              values.linkedService === 'FOAMfrat' ? null : values.linkedService,
            pageRank: values.pageRank,
          })
          .then(() => dispatch(fetchSections()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Creating Section')
            dispatch(fetchSections())
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put('/api/v2/create/section', {
            id: values.id,
            name: values.name,
            description: values.description,
            libraryId: '0f6846fb-027d-4bde-899a-e8beb373e668',
            linkedServiceId:
              values.linkedService === 'FOAMfrat' ? null : values.linkedService,
            pageRank: values.pageRank,
          })
          .then(() => dispatch(fetchSections()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Updating Section')
            dispatch(fetchSections())
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (sectionsStatus === 'uninitialized') {
      dispatch(fetchSections())
    }
  }, [sectionsStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (!formOpen) {
      form.resetForm()
    }
  }, [formOpen])

  useEffect(() => {
    if (activeLinkedService !== 'unselected') {
      form.setFieldValue('linkedService', activeLinkedService)
    } else {
      form.setFieldValue('linkedService', '')
    }
  }, [activeLinkedService])

  let onEdit = (id: string) => {
    let section = sections.find((s) => s.id === id)
    form.setFieldValue('id', section?.id)
    form.setFieldValue('name', section?.name)
    form.setFieldValue('description', section?.description)
    form.setFieldValue('pageRank', section?.pageRank ?? 0)
    form.setFieldValue('linkedService', section?.linkedServiceID ?? 'FOAMfrat')
    setFormOpen(true)
  }

  let onImageUpload = (id: string, t: any) => {
    setUploading(id)
    return axios
      .put('/api/v2/create/section/image', { id })
      .then((response) => uploadImageToS3(t, response.data))
      .then(() => dispatch(fetchSections()))
      .then(() => setUploading(''))
      .catch((err) => {
        setUploading('')
        setErrorMessage(err?.message || 'Error Uploading Image')
      })
  }

  return (
    <>
      <Box component="main" sx={{ p: 3 }}>
        <Typography
          variant="h4"
          className="my-4 d-inline"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Sections
        </Typography>
        <Typography paragraph className="ml-3 d-inline">
          Create a section or select one to edit
        </Typography>
        <div style={{ margin: '16px 0' }}>
          <SelectLinkedService />
        </div>
        <div>
          <Grid container spacing={2}>
            <Grid item xl={4} md={6} xs={12}>
              <Paper
                sx={{
                  p: 2,
                  margin: 'auto',
                  flexGrow: 1,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6} sm container>
                    <Grid item xs container direction="column" spacing={2}>
                      <Grid item xs>
                        <Typography
                          gutterBottom
                          variant="subtitle1"
                          component="div"
                        >
                          <Skeleton animation={false} />
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <Skeleton animation={false} />
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Skeleton animation={false} />
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Skeleton animation={false} />
                        </Typography>
                      </Grid>
                      <Grid item>
                        <div className="flex flex-row-reverse">
                          <Button
                            className="ml-3"
                            startIcon={<AddIcon />}
                            variant="contained"
                            onClick={compose(setFormOpen, T)}
                          >
                            Add New Section
                          </Button>
                        </div>
                      </Grid>
                    </Grid>
                    <Grid item>{/* Nothing here */}</Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            {sections.map((s, i) => (
              <Grid key={`${s.name}-${i}`} item xl={4} md={6} xs={12}>
                <Paper
                  sx={{
                    p: 2,
                    margin: 'auto',
                    flexGrow: 1,
                  }}
                  style={{
                    backgroundImage: `url(${s.imageName})`,
                    backgroundSize: 'cover',
                    backgroundPosition: '0px 48%',
                    boxShadow: 'inset 0 0 0 1000px rgba(37, 43, 66, 0.94)',
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm container>
                      <Grid item xs container direction="column" spacing={2}>
                        <Grid item xs container direction="row">
                          <Grid item xs>
                            <Typography
                              gutterBottom
                              variant="subtitle1"
                              component="div"
                            >
                              {s.name}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              {s.description || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Courses:{' '}
                              {courses?.filter((c) => c.sectionID === s.id)
                                ?.length ?? 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Page Rank: {s.pageRank ?? 0}
                            </Typography>
                          </Grid>
                          <Grid item xs>
                            <Box
                              sx={{
                                pl: 1,
                                mt: 1,
                                display: 'flex',
                                flexDirection: 'row-reverse',
                                flexWrap: 'wrap',
                                gap: 1,
                              }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CourseIcon />}
                                onClick={() => {
                                  dispatch(activeSectionChanged(s.id))
                                  navigate('/create/course')
                                }}
                              >
                                Manage Courses
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <Box
                            sx={{
                              pl: 2,
                              display: 'flex',
                              flexDirection: 'row-reverse',
                              flexWrap: 'wrap',
                              gap: 1,
                              alignItems: 'center',
                            }}
                          >
                            <Tooltip title="Delete">
                              <Button
                                variant="outlined"
                                onClick={() => setDeleting(`delete-${s.id}`)}
                              >
                                <DeleteForeverIcon />
                              </Button>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <Button
                                variant="outlined"
                                onClick={() => onEdit(s.id)}
                              >
                                <EditIcon />
                              </Button>
                            </Tooltip>
                            {uploading !== s.id ? (
                              <Tooltip title="Upload Thumbnail">
                                <Button variant="outlined" component="label">
                                  <AddImageIcon onClick={noop} />
                                  <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png"
                                    hidden
                                    onChange={(event) =>
                                      processImageFile(event, {
                                        onLoadingStart: noop,
                                        onLoadingEnd: (t: any) => {
                                          onImageUpload(s.id, t)
                                        },
                                      })
                                    }
                                  />
                                </Button>
                              </Tooltip>
                            ) : (
                              <CircularProgress size={28} />
                            )}
                          </Box>
                        </Grid>
                        <ConfirmAction
                          executeAction={() =>
                            axios
                              .delete(
                                `/api/v2/create/delete-section?sectionID=${s.id}`,
                              )
                              .then(() => dispatch(fetchSections()))
                              .then(() => {
                                if (activeSection === s.id) {
                                  dispatch(activeSectionChanged('unselected'))
                                }
                              })
                          }
                          open={deleting}
                          setOpen={setDeleting}
                          openText={`delete-${s.id}`}
                          title="Confirm Deletion"
                          text={`Are you sure you want to delete ${s.name}?`}
                          onFailure={(err) =>
                            setErrorMessage(
                              err.message || 'Error Deleting Section',
                            )
                          }
                        />
                      </Grid>
                      {/* <Grid item>Nothing here</Grid> */}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </div>

        <Drawer
          anchor="bottom"
          open={formOpen && !saving}
          onClose={compose(setFormOpen, F)}
        >
          <Container maxWidth="sm">
            <Card className="m-4">
              <form className="p-4" onSubmit={form.handleSubmit}>
                <TextField
                  fullWidth
                  className="mt-4"
                  variant="standard"
                  value={form.values.name}
                  label="Name"
                  name="name"
                  onChange={form.handleChange}
                  error={form.touched.name && !!form.errors.name}
                />

                <TextField
                  fullWidth
                  className="mt-4"
                  variant="standard"
                  value={form.values.description}
                  label="Description"
                  name="description"
                  onChange={form.handleChange}
                  error={form.touched.description && !!form.errors.description}
                />

                <TextField
                  fullWidth
                  className="mt-4"
                  variant="standard"
                  disabled
                  value="Self-Paced"
                  label="Library"
                  name="library"
                  error={form.touched.library && !!form.errors.library}
                />

                <FormControl fullWidth variant="standard" className="mt-4">
                  <InputLabel id="demo-simple-select-label">
                    Associated linked service
                  </InputLabel>
                  <Select
                    value={form.values.linkedService}
                    label="Associated linked service"
                    onChange={(e: any) =>
                      form.setFieldValue('linkedService', e.target.value)
                    }
                    error={
                      form.touched.linkedService && !!form.errors.linkedService
                    }
                  >
                    {linkedServices.reduce(
                      (acc: ReactElement[], ls) => {
                        // eslint-disable-next-line fp/no-mutating-methods
                        acc.push(
                          <MenuItem key={ls.id} value={ls.id}>
                            {ls.name}
                          </MenuItem>,
                        )
                        return acc
                      },
                      session?.roles.includes('admin')
                        ? [
                          <MenuItem key="FF" value={'FOAMfrat'}>
                            FOAMfrat
                          </MenuItem>,
                        ]
                        : [],
                    )}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  type="number"
                  className="mt-4"
                  variant="standard"
                  value={form.values.pageRank}
                  label="Page rank (lower numbers appear first on Self-Paced page)"
                  name="pageRank"
                  onChange={form.handleChange}
                  error={form.touched.pageRank && !!form.errors.pageRank}
                />

                <div className="flex flex-row-reverse">
                  <Button
                    variant="contained"
                    color="primary"
                    className="mt-4 mr-3"
                    type="submit"
                  >
                    Submit
                  </Button>
                </div>
              </form>
            </Card>
          </Container>
        </Drawer>
      </Box>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[coursesStatus, linkedServicesStatus, sectionsStatus]}
        loading={saving}
      />
    </>
  )
}
